import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { BarChart3, CheckSquare, DollarSign, Trophy, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../utils/api';
import { Draft } from '../types';
import { Button, Card, Container, LoadingSpinner, colors, media, spacing, typography } from '../styles/GlobalStyles';

const ReportsContainer = styled.div`
  min-height: calc(100vh - 80px);
  background: ${colors.black};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[6]};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  color: ${colors.white};
  font-size: ${typography.fontSize['3xl']};
  margin: 0 0 ${spacing[1]};
`;

const Subtitle = styled.p`
  color: ${colors.grey[400]};
  margin: 0;
`;

const DraftSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[2]};
  margin: ${spacing[4]} 0;

  ${media.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const DraftOption = styled.label.withConfig({
  shouldForwardProp: (prop) => prop !== '$selected'
})<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]};
  min-width: 0;
  background: ${props => props.$selected ? `${colors.green}18` : colors.grey[900]};
  border: 1px solid ${props => props.$selected ? colors.green : colors.grey[700]};
  border-radius: 0.375rem;
  color: ${colors.white};
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  accent-color: ${colors.green};
`;

const DraftName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${spacing[3]};
  margin: ${spacing[6]} 0;

  ${media.tablet} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const Metric = styled.div`
  padding: ${spacing[4]};
  border: 1px solid ${colors.grey[700]};
  border-radius: 0.375rem;
  background: ${colors.grey[950]};
`;

const MetricLabel = styled.div`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.xs};
`;

const MetricValue = styled.div`
  color: ${colors.white};
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.bold};
  margin-top: ${spacing[1]};
`;

const ReportGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[4]};

  ${media.desktop} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ReportPanel = styled(Card)`
  padding: ${spacing[4]};
`;

const PanelTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${colors.white};
  font-size: ${typography.fontSize.lg};
  margin: 0 0 ${spacing[3]};
`;

const ReportRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${spacing[3]};
  padding: ${spacing[2]} 0;
  border-bottom: 1px solid ${colors.grey[800]};
  color: ${colors.grey[300]};
  font-size: ${typography.fontSize.sm};

  &:last-child {
    border-bottom: 0;
  }
`;

const PrimaryText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyState = styled(Card)`
  padding: ${spacing[8]};
  color: ${colors.grey[400]};
  text-align: center;
`;

const ReportsPage: React.FC = () => {
  const [completedDrafts, setCompletedDrafts] = useState<Draft[]>([]);
  const [selectedDraftIds, setSelectedDraftIds] = useState<string[]>([]);
  const [reportDrafts, setReportDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningReports, setRunningReports] = useState(false);

  useEffect(() => {
    const loadCompletedDrafts = async () => {
      try {
        const response = await apiClient.drafts.getAll();
        if (response.success) {
          const completed = response.data.drafts.filter((draft: Draft) => draft.status === 'completed');
          setCompletedDrafts(completed);
          setSelectedDraftIds(completed.map((draft: Draft) => draft._id));
        }
      } catch (error) {
        console.error('Failed to load completed drafts:', error);
        toast.error('Unable to load completed drafts');
      } finally {
        setLoading(false);
      }
    };

    loadCompletedDrafts();
  }, []);

  const toggleDraft = (draftId: string) => {
    setSelectedDraftIds((previous) => (
      previous.includes(draftId)
        ? previous.filter((id) => id !== draftId)
        : [...previous, draftId]
    ));
  };

  const runReports = async () => {
    if (selectedDraftIds.length === 0) {
      toast.error('Select at least one completed draft');
      return;
    }

    try {
      setRunningReports(true);
      const responses = await Promise.all(selectedDraftIds.map((draftId) => apiClient.drafts.getById(draftId)));
      setReportDrafts(responses.filter((response) => response.success).map((response) => response.data.draft));
    } catch (error) {
      console.error('Failed to run reports:', error);
      toast.error('Unable to load the selected draft reports');
    } finally {
      setRunningReports(false);
    }
  };

  const reportPicks = reportDrafts.flatMap((draft) => draft.picks
    .filter((pick) => !pick.isUndone)
    .map((pick) => ({
      ...pick,
      draftName: draft.name,
      teamName: typeof pick.teamId === 'string' ? 'Unknown team' : pick.teamId?.name || 'Unknown team'
    })));
  const totalSpend = reportPicks.reduce((total, pick) => total + pick.cost, 0);
  const positionCounts = reportPicks.reduce<Record<string, number>>((counts, pick) => {
    counts[pick.position] = (counts[pick.position] || 0) + 1;
    return counts;
  }, {});
  const teamSpending = reportPicks.reduce<Record<string, { name: string; spend: number; players: number }>>((teams, pick) => {
    const key = `${pick.draftName}-${pick.teamName}`;
    const team = teams[key] || { name: `${pick.teamName} (${pick.draftName})`, spend: 0, players: 0 };
    team.spend += pick.cost;
    team.players += 1;
    teams[key] = team;
    return teams;
  }, {});
  const topPicks = [...reportPicks].sort((left, right) => right.cost - left.cost).slice(0, 8);
  const topTeams = Object.values(teamSpending).sort((left, right) => right.spend - left.spend).slice(0, 8);

  if (loading) {
    return <ReportsContainer><Container><LoadingSpinner size="lg" /></Container></ReportsContainer>;
  }

  return (
    <ReportsContainer>
      <Container>
        <Header>
          <div>
            <Title>Draft Reports</Title>
            <Subtitle>Compare completed drafts using their recorded picks and auction prices.</Subtitle>
          </div>
          <Button onClick={runReports} disabled={runningReports || selectedDraftIds.length === 0}>
            <BarChart3 size={18} />
            {runningReports ? 'Running...' : 'Run Reports'}
          </Button>
        </Header>

        {completedDrafts.length === 0 ? (
          <EmptyState><Trophy size={40} /><p>No completed drafts are available for reporting.</p></EmptyState>
        ) : (
          <>
            <Card padding={spacing[4]}>
              <PanelTitle><CheckSquare size={20} /> Completed Drafts</PanelTitle>
              <DraftSelector>
                {completedDrafts.map((draft) => (
                  <DraftOption key={draft._id} $selected={selectedDraftIds.includes(draft._id)}>
                    <Checkbox type="checkbox" checked={selectedDraftIds.includes(draft._id)} onChange={() => toggleDraft(draft._id)} />
                    <DraftName>{draft.name}</DraftName>
                  </DraftOption>
                ))}
              </DraftSelector>
            </Card>

            {reportDrafts.length > 0 && (
              <>
                <MetricGrid>
                  <Metric><MetricLabel>Drafts Analyzed</MetricLabel><MetricValue>{reportDrafts.length}</MetricValue></Metric>
                  <Metric><MetricLabel>Players Drafted</MetricLabel><MetricValue>{reportPicks.length}</MetricValue></Metric>
                  <Metric><MetricLabel>Total Spend</MetricLabel><MetricValue>${totalSpend}</MetricValue></Metric>
                  <Metric><MetricLabel>Average Pick</MetricLabel><MetricValue>${reportPicks.length ? (totalSpend / reportPicks.length).toFixed(0) : 0}</MetricValue></Metric>
                </MetricGrid>

                <ReportGrid>
                  <ReportPanel>
                    <PanelTitle><Users size={20} /> Position Mix</PanelTitle>
                    {Object.entries(positionCounts).sort(([left], [right]) => left.localeCompare(right)).map(([position, count]) => (
                      <ReportRow key={position}><PrimaryText>{position}</PrimaryText><span>{count} players</span></ReportRow>
                    ))}
                  </ReportPanel>
                  <ReportPanel>
                    <PanelTitle><DollarSign size={20} /> Highest-Cost Picks</PanelTitle>
                    {topPicks.map((pick) => (
                      <ReportRow key={`${pick.draftName}-${pick.pickNumber}`}><PrimaryText>{pick.playerName} · {pick.teamName} · {pick.draftName}</PrimaryText><span>${pick.cost}</span></ReportRow>
                    ))}
                  </ReportPanel>
                  <ReportPanel>
                    <PanelTitle><Trophy size={20} /> Team Spending</PanelTitle>
                    {topTeams.map((team) => (
                      <ReportRow key={team.name}><PrimaryText>{team.name}</PrimaryText><span>${team.spend} · {team.players} players</span></ReportRow>
                    ))}
                  </ReportPanel>
                </ReportGrid>
              </>
            )}
          </>
        )}
      </Container>
    </ReportsContainer>
  );
};

export default ReportsPage;